/**
 * OMR Card Layout Component
 * Main component that renders a complete OMR (Optical Mark Recognition) answer card
 * Suitable for printing and scanning with OpenCV
 *
 * Props:
 * - examId: number - ID of the exam
 * - examName: string - Name/title of the exam
 * - questions: array - Array of question objects {number, title}
 * - studentIdFormat: string - "5digits" (default)
 * - alternativesPerQuestion: number - Usually 5 (A,B,C,D,E)
 */

import React, { useEffect } from 'react';
import OMRFiducials from './OMRFiducials';
import OMRStudentId from './OMRStudentId';
import OMRTimingMarks from './OMRTimingMarks';
import OMRBubbles from './OMRBubbles';
import {
  PAGE_WIDTH_MM,
  PAGE_HEIGHT_MM,
  PAGE_MARGIN_MM,
  QUESTIONS_START_Y_MM,
  QUESTION_LINE_HEIGHT_MM,
} from '../../utils/omr/constants';
import { getQuestionsLayout, validateLayout } from '../../utils/omr/geometryCalc';
import styles from './OMRCardLayout.module.css';

const OMRCardLayout = ({
  examId = 1,
  examName = 'Exame de Avaliação',
  questions = [],
  studentIdFormat = 'cpf',
  alternativesPerQuestion = 5,
}) => {
  // Validate layout on mount
  useEffect(() => {
    validateLayout(questions.length, QUESTIONS_START_Y_MM);
  }, [questions.length]);

  // Calculate question positions
  const questionsLayout = getQuestionsLayout(questions, QUESTIONS_START_Y_MM);

  return (
    <div className={styles.omrCardContainer}>
      {/* This container is positioned relative and uses mm units via CSS */}
      <div
        className={styles.omrCard}
        data-testid="omr-card-root"
        style={{
          width: `${PAGE_WIDTH_MM}mm`,
          height: `${PAGE_HEIGHT_MM}mm`,
          padding: 0,
        }}
      >
        {/* ===== HEADER SECTION ===== */}
        <div className={styles.header}>
          <div className={styles.schoolInfo}>
            <div className={styles.schoolName}>ESCOLA / SISTEMA DE AVALIAÇÃO</div>
            <div className={styles.examCode}>CÓDIGO DA PROVA: {examId.toString().padStart(3, '0')}</div>
          </div>

          <div className={styles.examInfo}>
            <div className={styles.examName}>{examName}</div>
            <div className={styles.instructions}>
              Preencha completamente cada bolinha com caneta azul ou preta
            </div>
          </div>
        </div>

        {/* ===== FIDUCIALS (Registration Marks) ===== */}
        <OMRFiducials />

        {/* ===== STUDENT ID SECTION ===== */}
        <OMRStudentId />

        {/* ===== QUESTIONS SECTION ===== */}
        <div
          className={styles.questionsSection}
          style={{
            position: 'relative',
            top: `${QUESTIONS_START_Y_MM}mm`,
            width: '100%',
            minHeight: `${questions.length * QUESTION_LINE_HEIGHT_MM}mm`,
          }}
        >
          {/* Timing Marks */}
          <OMRTimingMarks numberOfQuestions={questions.length} />

          {/* Question rows */}
          {questionsLayout.map((q) => (
            <div
              key={`question-row-${q.number}`}
              className={styles.questionRow}
              style={{
                position: 'absolute',
                left: q.columnNumber === 0 ? `${PAGE_MARGIN_MM}mm` : 'calc(50% + 5mm)',
                top: `${q.rowInColumn * QUESTION_LINE_HEIGHT_MM}mm`,
                width: 'calc(50% - 5mm)',
                height: `${q.height}mm`,
                display: 'flex',
                alignItems: 'center',
                gap: '3mm',
              }}
              data-testid={`question-${q.number}`}
            >
              {/* Question number */}
              <div
                style={{
                  minWidth: '20mm',
                  textAlign: 'right',
                  paddingRight: '2mm',
                  fontSize: '9pt',
                  fontWeight: 'bold',
                }}
              >
                {q.number.toString().padStart(2, '0')}.
              </div>

              {/* Answer bubbles (A, B, C, D, E) */}
              <div style={{ position: 'relative', flex: 1, height: '100%' }}>
                <OMRBubbles
                  questionYMm={0}
                  questionNumber={q.number}
                />
              </div>
            </div>
          ))}
        </div>

        {/* ===== FOOTER SECTION ===== */}
        <div
          className={styles.footer}
          style={{
            position: 'absolute',
            bottom: `${PAGE_MARGIN_MM}mm`,
            width: '100%',
            textAlign: 'center',
            fontSize: '8pt',
            color: '#666666',
          }}
        >
          <div>NÃO UTILIZE LÁPIS. PREENCHA COMPLETAMENTE AS BOLINHAS.</div>
          <div style={{ marginTop: '2mm' }}>
            SISTEMA AUTOMÁTICO DE LEITURA ÓPT ICA
          </div>
        </div>
      </div>
    </div>
  );
};

export default OMRCardLayout;
